import { Close, HelpOutline } from '@mui/icons-material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import {
  AppBar,
  Backdrop,
  Box,
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { sentryLog } from '../../config/sentry';
import {
  changeHeaderColorByEnv,
  gray10,
  primary100,
  white
} from '../../constants/Colors';
import { userType as userTypeEnum } from '../../constants/Enum';
import { MANUAL_MOVIE_URL } from '../../constants/Links';
import { sideBarWidth, spHeaderHeight } from '../../constants/Location';
import { AuthContext } from '../../contexts/auth';

import logoImage from '../assets/cmcos_logo_white.png';
import Navipanel from './NaviPanel';

const getStyles = (isMobile: boolean) => {
  return {
    button: {
      '&.Mui-disabled': {
        '.MuiListItemText-root': {
          '.MuiTypography-root': {
            fontWeight: '700'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.24)',
        opacity: 1
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.24)'
      },
      columnGap: '24px',
      height: '48px',
      width: '240px'
    },
    buttonText: {
      '.MuiTypography-root': {
        fontSize: '16px'
      },
      color: '#FFF'
    },
    hamburgerMenu: {
      padding: 0,
      color: '#fff'
    },
    header: {
      bgcolor: changeHeaderColorByEnv(),
      width: '100%'
    },
    headerTitle: {
      flexGrow: 1,
      fontSize: '23px',
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'space-between',
      mr: '12px',
      minHeight: { xs: spHeaderHeight }
    },
    headerWrapper: {
      flexGrow: 1,
      height: spHeaderHeight,
      position: 'sticky',
      top: 0,
      zIndex: 999
    },
    icon: {
      color: '#FFF',
      width: '24px'
    },
    itemIcon: {
      minWidth: '24px'
    },
    link: {
      textDecoration: 'none'
    },
    list: {
      padding: isMobile ? '0' : '15px 0 0 0'
    },
    listItem: {
      marginBottom: isMobile ? '0' : '16px',
      padding: 0,
      flexDirection: 'column'
    },
    logoPc: {
      cursor: 'pointer',
      width: '80px'
    },
    logoSp: {
      cursor: 'pointer',
      width: '80px'
    },
    bottomButtons: {
      bottom: 0,
      position: 'absolute',
      zIndex: 10
    },
    sideBarWrapper: {
      backgroundColor: changeHeaderColorByEnv(),
      height: isMobile ? `calc(100vh - ${spHeaderHeight})` : '100vh',
      top: isMobile ? spHeaderHeight : 0,
      left: isMobile ? 'unset' : 0,
      right: isMobile ? 0 : 'unset',
      zIndex: isMobile ? 99 : 'unset',
      position: 'fixed',
      width: sideBarWidth,
      overflow: 'unset',
      ml: isMobile ? 'auto' : 0
    },
    topTitleWrapper: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.24)',
      padding: '22px 24px'
    },
    subMenuWrapper: {
      position: 'fixed',
      left: sideBarWidth,
      bgcolor: changeHeaderColorByEnv(),
      zIndex: 100,
      ml: '2px',
      p: '4px',
      borderRadius: '4px'
    },
    trialLimitDisplay: {
      m: '0 16px 16px 16px',
      p: '16px',
      backgroundColor: primary100,
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.24)'
      }
    },
    trialLimitDisplaySp: {
      m: '16px'
    },
    trialLimitText: {
      fontSize: isMobile ? '12px' : '14px',
      color: white,
      whiteSpace: 'pre-line'
    },
    trialLimitArrow: {
      color: white,
      fontSize: '14px'
    },
    listItemDivider: {
      bgcolor: gray10,
      opacity: 0.2,
      display: isMobile ? 'block' : 'none'
    },
    videoDialog: {
      borderRadius: 0,
      maxWidth: isMobile ? 'unset' : 'none',
      overflow: 'visible'
    },
    video: {
      width: isMobile ? '100%' : '880px',
      height: 'auto'
    },
    closeIconButton: {
      color: white,
      width: '24px',
      height: '24px',
      top: '-32px',
      right: '0px',
      position: 'absolute'
    }
  };
};

const SideBar: React.FC = () => {
  const currentPage = window.location.pathname;
  const isMobile: boolean = useMediaQuery({ query: '(max-width: 960px)' });
  const styles = getStyles(isMobile);
  const { logout, userId, userRank, userType, loginStatus } =
    useContext(AuthContext);
  const [openNavi, setOpenNavi] = useState<boolean>(false);

  const sideBarText = {
    home: 'ホーム',
    news: 'お知らせ',
    material: '建材検索',
    mylist: 'マイリスト',
    favorite: 'お気に入り',
    product: '建材登録',
    setting: 'ユーザー設定',
    standard: 'CO2基準値',
    evaluation: '評価した建材',
    members: '共有者一覧',
    account: 'アカウント管理',
    logout: 'ログアウト',
    operator: '事務局',
    operatorProduct: '建材管理',
    operatorMaterials: '部材管理',
    contact: 'お問い合わせ',
    manual: '使用マニュアル',
    help: 'ヘルプ',
    explainMovie: '解説動画',
    explainConvertedValue: '換算値説明'
  };

  interface SubMenu {
    icon: React.ReactNode;
    link?: string;
    onClick?: () => void;
    text: string;
    iconPosition?: 'left' | 'right';
  }

  const buttonList = [
    {
      icon: <HomeOutlinedIcon sx={styles.icon} />,
      link: '/home',
      text: sideBarText.home
    },
    {
      icon: <NotificationsNoneOutlinedIcon sx={styles.icon} />,
      link: '/news',
      text: sideBarText.news
    },
    {
      icon: <SearchOutlinedIcon sx={styles.icon} />,
      link: '/material',
      text: sideBarText.material
    },
    {
      icon: <AssignmentOutlinedIcon sx={styles.icon} />,
      link: '/mylist',
      text: sideBarText.mylist
    },
    {
      icon: <FavoriteBorderOutlinedIcon sx={styles.icon} />,
      link: '/favorite',
      text: sideBarText.favorite
    },
    {
      icon: <CreateOutlinedIcon sx={styles.icon} />,
      link: '/product',
      text: sideBarText.product
    },
    {
      icon: <SettingsOutlinedIcon sx={styles.icon} />,
      link: '',
      text: sideBarText.setting,
      pullDown: true,
      subMenu: [
        {
          icon: <BarChartOutlinedIcon sx={styles.icon} />,
          link: '/standard',
          text: sideBarText.standard,
          iconPosition: 'left'
        },
        // 評価機能は一時的に非表示
        // {
        //   icon: <ChatBubbleOutlineOutlinedIcon sx={styles.icon} />,
        //   link: '/evaluation',
        //   text: sideBarText.evaluation
        // },
        {
          icon: <PeopleOutlinedIcon sx={styles.icon} />,
          link: '/members',
          text: sideBarText.members,
          iconPosition: 'left'
        },
        {
          icon: <AccountCircleOutlinedIcon sx={styles.icon} />,
          link: '/account',
          text: sideBarText.account,
          iconPosition: 'left'
        },
        {
          icon: <LogoutIcon sx={styles.icon} />,
          link: '',
          text: sideBarText.logout,
          iconPosition: 'left'
        }
      ] as SubMenu[],
      subMenuType: 'setting'
    },
    {
      icon: <SettingsOutlinedIcon sx={styles.icon} />,
      link: '',
      text: sideBarText.operator,
      pullDown: true,
      subMenu: [
        {
          icon: <BarChartOutlinedIcon sx={styles.icon} />,
          link: '/operator/material',
          text: sideBarText.operatorProduct,
          iconPosition: 'left'
        },
        {
          icon: <ChatBubbleOutlineOutlinedIcon sx={styles.icon} />,
          link: '/operator/materials',
          text: sideBarText.operatorMaterials,
          iconPosition: 'left'
        }
      ] as SubMenu[],
      subMenuType: 'admin'
    }
  ];

  const helpButton = {
    icon: <HelpOutline sx={styles.icon} />,
    link: '',
    text: sideBarText.help,
    pullDown: true,
    subMenu: [
      {
        icon: <OpenInNewOutlinedIcon sx={styles.icon} />,
        link: 'https://cmcos-manual.s3.ap-northeast-1.amazonaws.com/CMCOS%E4%BD%BF%E7%94%A8%E3%83%9E%E3%83%8B%E3%83%A5%E3%82%A2%E3%83%AB.pdf',
        text: sideBarText.manual,
        iconPosition: 'right'
      },
      {
        icon: <OpenInNewOutlinedIcon sx={styles.icon} />,
        onClick: () => {
          setManualMovieOpen(true);
        },
        text: sideBarText.explainMovie,
        iconPosition: 'right'
      },
      {
        icon: <OpenInNewOutlinedIcon sx={styles.icon} />,
        onClick: () => {
          setOpenNavi(true);
        },
        text: sideBarText.explainConvertedValue,
        iconPosition: 'right'
      }
    ] as SubMenu[],
    subMenuType: 'help'
  };

  const contactButton = {
    icon: <OpenInNewOutlinedIcon sx={styles.icon} />,
    link: '/contact',
    text: sideBarText.contact
  };

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [manualMovieOpen, setManualMovieOpen] = React.useState(false);

  const [subMenuPos, setSubMenuPos] = React.useState<{
    top: number | undefined;
    bottom: number | undefined;
  } | null>(null);
  const [subMenu, setSubMenu] = React.useState<SubMenu[]>([]);
  const [openedMobileSubMenu, setOpenedMobileSubMenu] = React.useState<
    'setting' | 'admin' | 'help' | null
  >(null);

  const currentUserType = Number(localStorage.getItem('user_type'));
  const [adminUser, setAdminUser] = React.useState(false);
  const [makerUser, setMakerUser] = React.useState(false);

  // 事務局ユーザーチェック・建材メーカーチェック
  useEffect(() => {
    if (currentUserType === userTypeEnum.admin) setAdminUser(true);
    if (
      currentUserType === userTypeEnum.maker ||
      currentUserType === userTypeEnum.admin
    )
      setMakerUser(true);
  }, [currentUserType]);

  const isOpenPage = (link: string | undefined): boolean => {
    let result = false;
    if (link === '/operator/material') {
      result =
        currentPage === link ||
        currentPage.includes('/operator/material/') ||
        currentPage.includes('/operator/product') ||
        currentPage.includes('/operator/evaluation');
    } else if (
      link === '/evaluation' &&
      currentPage.includes('/operator/evaluation')
    ) {
      result = false;
    } else if (link) {
      result = currentPage === link || currentPage.includes(link);
    }
    return result;
  };

  const logoutButton = async () => {
    try {
      if (logout === undefined) return;
      await logout();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
    }
  };

  // ボタンの選択状態管理
  const selectedButton = (link: string) => {
    if (currentPage === link || currentPage.includes(link)) {
      if (currentPage.includes('/operator')) return;
      if (link === '/material' && currentPage.includes('news')) return;
      return {
        backgroundColor: 'rgba(255, 255, 255, 0.24)'
      };
    }
    return {};
  };

  const handleOpenSubMenu = (
    subMenu: SubMenu[],
    pos: { top: number | undefined; bottom: number | undefined },
    mobileSubMenu: 'setting' | 'admin' | 'help'
  ) => {
    if (isMobile) {
      setOpenedMobileSubMenu((prev) =>
        prev === mobileSubMenu ? null : mobileSubMenu
      );
    } else {
      if (subMenuPos && subMenuPos.top === pos.top) {
        setSubMenuPos(null);
      } else {
        setSubMenuPos(pos);
      }
    }

    setSubMenu(subMenu);
  };

  return (
    <>
      {/* SPヘッダー */}
      {isMobile && (
        <Box sx={styles.headerWrapper}>
          <AppBar position="fixed" sx={styles.header} elevation={0}>
            <Toolbar sx={styles.headerTitle}>
              <img
                src={logoImage}
                style={styles.logoSp}
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_NEXT_JS_BASE_URL}`,
                    '_blank'
                  )
                }
              />
              <IconButton
                edge="end"
                sx={styles.hamburgerMenu}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {!menuOpen ? <MenuIcon /> : <CloseOutlinedIcon />}
              </IconButton>
            </Toolbar>
          </AppBar>
        </Box>
      )}

      {/* サイドバー ハンバーガーメニュー */}
      {(!isMobile || (isMobile && menuOpen)) && (
        <>
          <Backdrop
            sx={{ color: '#fff', zIndex: 9 }}
            open={menuOpen}
            onClick={() => setMenuOpen(false)}
          ></Backdrop>
          <Grid sx={styles.sideBarWrapper}>
            {/* CMCOSロゴ - PCのみ表示 */}
            {!isMobile && (
              <Grid sx={styles.topTitleWrapper}>
                <img
                  src={logoImage}
                  style={styles.logoPc}
                  onClick={() =>
                    window.open(
                      `${process.env.REACT_APP_NEXT_JS_BASE_URL}`,
                      '_blank'
                    )
                  }
                />
              </Grid>
            )}

            <Grid
              sx={{
                height: 'calc(100vh - 128px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                webkitOverflowScrolling: 'touch',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Grid>
                <Divider sx={styles.listItemDivider} />
                <List sx={styles.list}>
                  {buttonList.map((button, index) => {
                    // サブメニューがある場合（ユーザー設定or事務局）
                    if (button.subMenu) {
                      if (button.text === sideBarText.operator && !adminUser)
                        return null; // 事務局以外は事務局ボタンを非表示
                      return (
                        <>
                          <ListItem sx={styles.listItem} key={index}>
                            <ListItemButton
                              sx={styles.button}
                              onClick={(event) => {
                                handleOpenSubMenu(
                                  button.subMenu || [],
                                  {
                                    top:
                                      button.subMenu.length > 2
                                        ? event.currentTarget.getBoundingClientRect()
                                            .top - 110
                                        : event.currentTarget.getBoundingClientRect()
                                            .top, // サブメニューアイテム2つ分の高さとマージンを考慮
                                    bottom: undefined
                                  },
                                  button.subMenuType as
                                    | 'setting'
                                    | 'admin'
                                    | 'help'
                                );
                              }}
                            >
                              <ListItemIcon sx={styles.itemIcon}>
                                {button.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={button.text}
                                sx={styles.buttonText}
                              />
                              <ListItemIcon sx={styles.itemIcon}>
                                {/*プルダウンアイコンの表示切り替え*/}
                                {!isMobile ? null : openedMobileSubMenu ===
                                  button.subMenuType ? (
                                  <KeyboardArrowUpOutlinedIcon
                                    sx={styles.icon}
                                  />
                                ) : (
                                  <KeyboardArrowDownOutlinedIcon
                                    sx={styles.icon}
                                  />
                                )}
                              </ListItemIcon>
                            </ListItemButton>
                          </ListItem>
                          {/* Mobile サブメニュー */}
                          {isMobile &&
                            openedMobileSubMenu === button.subMenuType && (
                              <Grid>
                                <Divider sx={styles.listItemDivider} />
                                {subMenu?.map((subButton, index) => {
                                  return subButton.text !== 'ログアウト' ? (
                                    <ListItem sx={styles.listItem} key={index}>
                                      <ListItemButton
                                        sx={{
                                          ...styles.button,
                                          ...{ width: '208px' },
                                          ...(isOpenPage(subButton.link)
                                            ? {
                                                backgroundColor:
                                                  'rgba(255, 255, 255, 0.24)'
                                              }
                                            : {})
                                        }}
                                        href={subButton.link || ''}
                                        target={
                                          subButton.link?.includes('http')
                                            ? '_blank'
                                            : undefined
                                        }
                                        onClick={() => {
                                          if (subButton.onClick) {
                                            subButton.onClick();
                                          }
                                        }}
                                      >
                                        {subButton.iconPosition === 'left' ? (
                                          <ListItemIcon sx={styles.itemIcon}>
                                            {subButton.icon}
                                          </ListItemIcon>
                                        ) : null}
                                        <ListItemText
                                          primary={subButton.text}
                                          sx={styles.buttonText}
                                        />
                                        {subButton.iconPosition === 'right' ? (
                                          <ListItemIcon sx={styles.itemIcon}>
                                            {subButton.icon}
                                          </ListItemIcon>
                                        ) : null}
                                      </ListItemButton>
                                    </ListItem>
                                  ) : (
                                    // ログアウトボタンの場合
                                    <ListItem sx={styles.listItem}>
                                      <ListItemButton
                                        sx={{
                                          ...styles.button,
                                          ...{ width: '208px' }
                                        }}
                                        onClick={async () =>
                                          await logoutButton()
                                        }
                                      >
                                        <ListItemIcon sx={styles.itemIcon}>
                                          <LogoutIcon sx={styles.icon} />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={'ログアウト'}
                                          sx={styles.buttonText}
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  );
                                })}
                              </Grid>
                            )}
                          <Divider sx={styles.listItemDivider} />
                        </>
                      );
                      // サブメニューがない場合
                    } else {
                      if (button.text === sideBarText.product && !makerUser)
                        return null; // 建材メーカー以外は建材登録ボタンを非表示
                      return (
                        <>
                          <ListItem sx={styles.listItem} key={index}>
                            <ListItemButton
                              sx={{
                                ...styles.button,
                                ...selectedButton(button.link)
                              }}
                              key={index}
                              href={button.link}
                            >
                              {button.text !== sideBarText.contact && (
                                <ListItemIcon sx={styles.itemIcon}>
                                  {button.icon}
                                </ListItemIcon>
                              )}
                              <ListItemText
                                primary={button.text}
                                sx={styles.buttonText}
                              />
                              {button.text === sideBarText.contact && (
                                <ListItemIcon sx={styles.itemIcon}>
                                  {button.icon}
                                </ListItemIcon>
                              )}
                            </ListItemButton>
                          </ListItem>
                          <Divider sx={styles.listItemDivider} />
                        </>
                      );
                    }
                  })}
                </List>
                {isMobile && (
                  <Grid sx={styles.bottomButtons}>
                    <Divider sx={{ bgcolor: '#fff', opacity: '24%' }} />
                    <Grid>
                      <Button
                        sx={[
                          styles.button,
                          {
                            '&:hover': {
                              borderRadius: '0px'
                            },
                            height: '64px',
                            justifyContent: 'start',
                            padding: '24px',
                            marginTop: '16px'
                          }
                        ]}
                        onClick={() => {
                          handleOpenSubMenu(
                            helpButton.subMenu || [],
                            {
                              top: 0,
                              bottom: 100
                            },
                            'help'
                          );
                        }}
                      >
                        {helpButton.icon}
                        <Typography sx={styles.buttonText}>
                          {helpButton.text}
                        </Typography>
                        <ListItemIcon sx={styles.itemIcon}>
                          {/*プルダウンアイコンの表示切り替え*/}
                          {!isMobile ? null : openedMobileSubMenu === 'help' ? (
                            <KeyboardArrowUpOutlinedIcon sx={styles.icon} />
                          ) : (
                            <KeyboardArrowDownOutlinedIcon sx={styles.icon} />
                          )}
                        </ListItemIcon>
                      </Button>
                      {/* ヘルプのサブメニュー */}
                      {isMobile && openedMobileSubMenu === 'help' && (
                        <Grid>
                          <Divider sx={styles.listItemDivider} />
                          {subMenu?.map((subButton, index) => {
                            return (
                              <ListItem sx={styles.listItem} key={index}>
                                <ListItemButton
                                  sx={{
                                    ...styles.button,
                                    ...{
                                      width: '208px'
                                    },
                                    ...(isOpenPage(subButton.link)
                                      ? {
                                          backgroundColor:
                                            'rgba(255, 255, 255, 0.24)'
                                        }
                                      : {})
                                  }}
                                  href={subButton.link || ''}
                                  target={
                                    subButton.link?.includes('http')
                                      ? '_blank'
                                      : undefined
                                  }
                                  onClick={() => {
                                    if (subButton.onClick) {
                                      subButton.onClick();
                                    }
                                  }}
                                >
                                  {subButton.iconPosition === 'left' ? (
                                    <ListItemIcon sx={styles.itemIcon}>
                                      {subButton.icon}
                                    </ListItemIcon>
                                  ) : null}
                                  <ListItemText
                                    primary={subButton.text}
                                    sx={styles.buttonText}
                                  />
                                  {subButton.iconPosition === 'right' ? (
                                    <ListItemIcon sx={styles.itemIcon}>
                                      {subButton.icon}
                                    </ListItemIcon>
                                  ) : null}
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </Grid>
                      )}
                      <Button
                        sx={[
                          styles.button,
                          {
                            '&:hover': {
                              borderRadius: '0px'
                            },
                            height: '64px',
                            justifyContent: 'space-between',
                            padding: '24px',
                            marginBottom: '16px'
                          }
                        ]}
                        href={contactButton.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          window.dataLayer.push({
                            event: 'click',
                            screen: window.location.pathname,
                            category: 'link',
                            action: 'sidebar_manual',
                            user_id: userId,
                            user_type: userType,
                            user_rank: userRank
                          });
                        }}
                      >
                        <Typography sx={styles.buttonText}>
                          {contactButton.text}
                        </Typography>
                        {contactButton.icon}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* 使用マニュアル - PCのみ表示 */}
            {!isMobile && (
              <Grid sx={styles.bottomButtons}>
                <Divider sx={{ bgcolor: '#fff', opacity: '24%' }} />
                <Grid>
                  <Button
                    sx={[
                      styles.button,
                      {
                        '&:hover': {
                          borderRadius: '0px'
                        },
                        height: '64px',
                        justifyContent: 'start',
                        padding: '24px'
                      }
                    ]}
                    onClick={() => {
                      handleOpenSubMenu(
                        helpButton.subMenu || [],
                        {
                          top: undefined,
                          bottom: 0
                        },
                        'help'
                      );
                    }}
                  >
                    {helpButton.icon}
                    <Typography sx={styles.buttonText}>
                      {helpButton.text}
                    </Typography>
                  </Button>
                  <Button
                    sx={[
                      styles.button,
                      {
                        '&:hover': {
                          borderRadius: '0px'
                        },
                        height: '64px',
                        justifyContent: 'space-between',
                        padding: '24px'
                      }
                    ]}
                    href={contactButton.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      window.dataLayer.push({
                        event: 'click',
                        screen: window.location.pathname,
                        category: 'link',
                        action: 'sidebar_manual',
                        user_id: userId,
                        user_type: userType,
                        user_rank: userRank
                      });
                    }}
                  >
                    <Typography sx={styles.buttonText}>
                      {contactButton.text}
                    </Typography>
                    {contactButton.icon}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* サブメニュー */}
      {!isMobile && subMenuPos && (
        <Box
          sx={[
            styles.subMenuWrapper,
            {
              top: subMenuPos.top,
              bottom: subMenuPos.bottom
            }
          ]}
        >
          <Grid sx={{ mt: '14px' }}>
            {subMenu?.map((subButton, index) => {
              return subButton.text !== 'ログアウト' ? (
                <ListItem sx={styles.listItem} key={index}>
                  <ListItemButton
                    sx={{
                      ...styles.button,
                      ...{ width: '208px' },
                      ...(isOpenPage(subButton.link)
                        ? {
                            backgroundColor: 'rgba(255, 255, 255, 0.24)'
                          }
                        : {})
                    }}
                    href={subButton.link || ''}
                    target={
                      subButton.link?.includes('http') ? '_blank' : undefined
                    }
                    onClick={() => {
                      if (subButton.onClick) {
                        subButton.onClick();
                      }
                    }}
                  >
                    {subButton.iconPosition === 'left' ? (
                      <ListItemIcon sx={styles.itemIcon}>
                        {subButton.icon}
                      </ListItemIcon>
                    ) : null}
                    <ListItemText
                      primary={subButton.text}
                      sx={styles.buttonText}
                    />
                    {subButton.iconPosition === 'right' ? (
                      <ListItemIcon sx={styles.itemIcon}>
                        {subButton.icon}
                      </ListItemIcon>
                    ) : null}
                  </ListItemButton>
                </ListItem>
              ) : (
                // ログアウトボタンの場合
                <ListItem sx={styles.listItem}>
                  <ListItemButton
                    sx={{
                      ...styles.button,
                      ...{ width: '208px' }
                    }}
                    onClick={async () => await logoutButton()}
                  >
                    <ListItemIcon sx={styles.itemIcon}>
                      <LogoutIcon sx={styles.icon} />
                    </ListItemIcon>
                    <ListItemText
                      primary={'ログアウト'}
                      sx={styles.buttonText}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Grid>
        </Box>
      )}
      <Dialog
        open={manualMovieOpen}
        onClose={() => {
          setManualMovieOpen(false);
        }}
        PaperProps={{ sx: styles.videoDialog }}
      >
        <Stack>
          <Box
            component="video"
            controls
            src={MANUAL_MOVIE_URL}
            style={styles.video}
          />
          <IconButton
            sx={styles.closeIconButton}
            onClick={() => {
              setManualMovieOpen(false);
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </Dialog>
      <Navipanel
        show={openNavi}
        userRole={loginStatus?.role || null}
        onClose={() => setOpenNavi(false)}
      />
    </>
  );
};

export default SideBar;
